/**
 * Dots of Care — shared building blocks.
 *  - CARE_TYPES: visual config per care type (icon, colors, label)
 *  - CareTimeline: vertical timeline of care moments
 *  - CareMomentDialog: add/edit dialog (customer + tour + type + details)
 *  - CareMomentsCard: drop-in card (used on Customer Profile & Tour Detail)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ContactSearchInput from '@/components/ContactSearchInput';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import { formatDate } from '@/lib/dateUtils';
import {
  Cake, Heart, Gift, UtensilsCrossed, HeartPulse, Sparkles,
  HandHeart, Trophy, Plus, Trash2, Compass, User, Loader2, ChevronDown, ChevronUp,
} from 'lucide-react';

export const CARE_TYPES = {
  'birthday': { label: 'Birthday Surprise', icon: Cake, dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  'anniversary': { label: 'Anniversary', icon: Heart, dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  'welcome-kit': { label: 'Welcome Kit', icon: Gift, dot: 'bg-teal-500', chip: 'bg-teal-50 text-teal-700 border-teal-200' },
  'special-meal': { label: 'Special Meal', icon: UtensilsCrossed, dot: 'bg-orange-500', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
  'medical': { label: 'Medical Care', icon: HeartPulse, dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200' },
  'upgrade': { label: 'Surprise Upgrade', icon: Sparkles, dot: 'bg-purple-500', chip: 'bg-purple-50 text-purple-700 border-purple-200' },
  'farewell': { label: 'Farewell Gift', icon: Gift, dot: 'bg-indigo-500', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  'milestone': { label: 'Milestone', icon: Trophy, dot: 'bg-yellow-500', chip: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  'other': { label: 'Other Care', icon: HandHeart, dot: 'bg-slate-500', chip: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const CareTimeline = ({ moments, onDelete, showCustomer = false, showTour = true }) => {
  const navigate = useNavigate();
  if (!moments.length) return null;
  return (
    <div className="relative pl-5" data-testid="care-timeline">
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
      <div className="space-y-4">
        {moments.map((m) => {
          const cfg = CARE_TYPES[m.care_type] || CARE_TYPES.other;
          const Icon = cfg.icon;
          return (
            <div key={m.id} className="relative" data-testid={`care-moment-${m.id}`}>
              <span className={`absolute -left-5 top-1 w-4 h-4 rounded-full ${cfg.dot} ring-4 ring-white flex items-center justify-center`}>
                <Icon className="w-2.5 h-2.5 text-white" />
              </span>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${cfg.chip}`}>
                  <Icon className="w-2.5 h-2.5" />{cfg.label}
                </span>
                <span className="text-[11px] text-muted-foreground">{formatDate(m.date)}</span>
                {onDelete && (
                  <button onClick={() => onDelete(m)} className="ml-auto p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600" data-testid={`care-delete-${m.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <p className="text-sm font-medium mt-0.5">{m.title}</p>
              {m.description && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{m.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-1">
                {showCustomer && m.customer_name && (
                  <button onClick={() => navigate(`/pollen-dots/customer/${m.customer_id}`)}
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white border hover:bg-primary/5 text-muted-foreground"
                    data-testid={`care-customer-link-${m.id}`}>
                    <User className="w-2.5 h-2.5" />{m.customer_name}
                  </button>
                )}
                {showTour && m.tour_id && (
                  <button onClick={() => navigate(`/pollen-dots/tour/${m.tour_id}`)}
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white border hover:bg-primary/5 text-muted-foreground">
                    <Compass className="w-2.5 h-2.5" />{m.tour_name || 'Tour'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const CareMomentDialog = ({ open, onOpenChange, preset = {}, onSaved }) => {
  const [customer, setCustomer] = useState(null);
  const [customerQuery, setCustomerQuery] = useState('');
  const [tourId, setTourId] = useState('');
  const [tours, setTours] = useState([]);
  const [careType, setCareType] = useState('birthday');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomer(preset.customerId ? { id: preset.customerId, name: preset.customerName || '' } : null);
    setCustomerQuery('');
    setTourId(preset.tourId || '');
    setCareType('birthday');
    setTitle('');
    setDescription('');
    setDate(new Date().toISOString().slice(0, 10));
  }, [open]);

  useEffect(() => {
    if (!open || preset.tourId) return;
    (async () => {
      try {
        const r = await fetch(`${API}/pollen/tours`, { credentials: 'include', headers: authHeaders() });
        if (r.ok) setTours(await r.json());
      } catch (e) { /* ignore */ }
    })();
  }, [open, preset.tourId]);

  const customerTours = tours.filter(t => customer && t.customer_id === customer.id);

  const save = async () => {
    if (!customer?.id) { toast.error('Pick a customer first'); return; }
    if (!title.trim()) { toast.error('Give this care moment a title'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/pollen/care-moments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ customer_id: customer.id, tour_id: tourId, care_type: careType, title, description, date }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      toast.success('Dot of Care logged 💛');
      onOpenChange(false);
      onSaved && onSaved(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandHeart className="w-4 h-4 text-rose-600" />Log a Dot of Care</DialogTitle>
          <DialogDescription>A small act of care that made a traveller&apos;s day.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Traveller</Label>
            {preset.customerId ? (
              <p className="text-sm font-medium mt-1" data-testid="care-preset-customer">{preset.customerName || 'Selected customer'}</p>
            ) : customer ? (
              <div className="flex items-center justify-between mt-1">
                <p className="text-sm font-medium">{customer.name}</p>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { setCustomer(null); setCustomerQuery(''); }}>Change</Button>
              </div>
            ) : (
              <ContactSearchInput value={customerQuery} onChange={setCustomerQuery}
                onSelect={(rec) => { if (rec.id) setCustomer({ id: rec.id, name: rec.name }); }}
                placeholder="Search customer..." testId="care-customer-search" />
            )}
          </div>
          {!preset.tourId && customer && customerTours.length > 0 && (
            <div>
              <Label className="text-xs">Tour (optional)</Label>
              <Select value={tourId || 'none'} onValueChange={(v) => setTourId(v === 'none' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="care-tour-select"><SelectValue placeholder="Link to a tour" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No tour</SelectItem>
                  {customerTours.map(t => <SelectItem key={t.id} value={t.id}>{t.tour_name || t.destination}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Care type</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {Object.entries(CARE_TYPES).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const active = careType === key;
                return (
                  <button key={key} type="button" onClick={() => setCareType(key)}
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${active ? cfg.chip + ' ring-1 ring-current' : 'bg-white text-muted-foreground hover:bg-muted'}`}
                    data-testid={`care-type-${key}`}>
                    <Icon className="w-3 h-3 shrink-0" /><span className="truncate">{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">What did we do?</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Surprise cake at Paro sunset dinner" data-testid="care-title-input" />
          </div>
          <div>
            <Label className="text-xs">Details (optional)</Label>
            <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="Anything the team should remember..." data-testid="care-description-input" />
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-40" data-testid="care-date-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="care-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Care Moment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CareMomentsCard = ({ customerId, customerName, tourId }) => {
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = tourId ? `tour_id=${tourId}` : `customer_id=${customerId}`;
      const r = await fetch(`${API}/pollen/care-moments?${p}`, { credentials: 'include', headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setMoments(data);
        if (data.length > 0 && data.length <= 3) setExpanded(true);
      }
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [customerId, tourId]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try {
      await fetch(`${API}/pollen/care-moments/${deleteTarget.id}`, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
      setMoments(prev => prev.filter(x => x.id !== deleteTarget.id));
      toast.success('Care moment removed');
    } catch (e) { toast.error('Failed to remove'); }
    setDeleteTarget(null);
  };

  if (loading) return null;

  return (
    <Card data-testid="care-moments-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <button onClick={() => setExpanded(v => !v)} className="flex items-center gap-2 text-left" data-testid="care-card-toggle">
          <CardTitle className="text-base flex items-center gap-2">
            <HandHeart className="w-4 h-4 text-rose-600" />Dots of Care ({moments.length})
          </CardTitle>
          {moments.length > 0 && (expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />)}
        </button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddOpen(true)} data-testid="care-add-btn">
          <Plus className="w-3 h-3 mr-1" />Log Care
        </Button>
      </CardHeader>
      {(expanded || moments.length === 0) && (
        <CardContent>
          {moments.length === 0 ? (
            <p className="text-xs text-muted-foreground">No care moments yet — log the first birthday cake, welcome kit or special touch here.</p>
          ) : (
            <CareTimeline moments={moments} onDelete={setDeleteTarget} showCustomer={!!tourId} showTour={!tourId} />
          )}
        </CardContent>
      )}
      <CareMomentDialog open={addOpen} onOpenChange={setAddOpen}
        preset={{ customerId, customerName, tourId }}
        onSaved={(m) => { setMoments(prev => [m, ...prev]); setExpanded(true); }} />
      <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Remove this care moment?" description={deleteTarget?.title || ''} onConfirm={confirmDelete} />
    </Card>
  );
};
